import type { Card, CardState, QueueResult, ReviewRating } from '../../types';

const LEARN_STEPS_MIN = [1, 10];
const GRAD_INTERVAL = 1;
const EASY_INTERVAL = 4;
const MIN_EASE = 1.3;
const HARD_INTERVAL_MULT = 1.2;
const EASY_BONUS = 1.3;

export interface ReviewContext {
  now?: Date;
  retentionTarget?: number;
  examMultiplier?: number;
}

export function createDefaultCardState(): CardState {
  return {
    ease: 2.5,
    interval: 0,
    reps: 0,
    lapses: 0,
    due: null,
    state: 'new',
    learnStep: 0,
    lastReviewed: null,
    reviewCount: 0,
    correctCount: 0,
  };
}

export function review(previous: CardState, q: ReviewRating, ctx: ReviewContext = {}): CardState {
  const now = ctx.now ?? new Date();
  const retentionTarget = ctx.retentionTarget ?? 0.9;
  const examMultiplier = ctx.examMultiplier ?? 1;
  const s: CardState = { ...previous };

  s.reviewCount = (s.reviewCount || 0) + 1;
  if (q >= 3) s.correctCount = (s.correctCount || 0) + 1;
  s.lastReviewed = now.toISOString();

  if (s.state === 'new' || s.state === 'learning') {
    if (q === 0) {
      s.state = 'learning';
      s.learnStep = 0;
      s.due = addMinutes(now, LEARN_STEPS_MIN[0]).toISOString();
    } else if (q === 3) {
      s.state = 'learning';
      s.due = addMinutes(now, Math.max(1, LEARN_STEPS_MIN[s.learnStep] || 5)).toISOString();
    } else if (q === 4) {
      const nextStep = (s.learnStep || 0) + 1;
      if (nextStep >= LEARN_STEPS_MIN.length) {
        s.state = 'review';
        s.interval = GRAD_INTERVAL;
        s.reps = 1;
        s.due = addDays(now, s.interval * examMultiplier).toISOString();
      } else {
        s.state = 'learning';
        s.learnStep = nextStep;
        s.due = addMinutes(now, LEARN_STEPS_MIN[nextStep]).toISOString();
      }
    } else if (q === 5) {
      s.state = 'review';
      s.interval = EASY_INTERVAL;
      s.reps = 1;
      s.ease = (s.ease || 2.5) + 0.05;
      s.due = addDays(now, s.interval * examMultiplier).toISOString();
    }
    return s;
  }

  let ease = s.ease || 2.5;
  let interval = s.interval || 1;
  let reps = s.reps || 0;

  if (q === 0) {
    s.state = 'learning';
    s.learnStep = 0;
    s.lapses = (s.lapses || 0) + 1;
    s.reps = 0;
    ease = Math.max(MIN_EASE, ease - 0.2);
    s.ease = ease;
    s.interval = Math.max(1, Math.round(interval * 0.3));
    s.due = addMinutes(now, LEARN_STEPS_MIN[0]).toISOString();
    return s;
  }

  if (q === 3) {
    ease = Math.max(MIN_EASE, ease - 0.15);
    interval = Math.max(1, Math.ceil(interval * HARD_INTERVAL_MULT));
  } else if (q === 4) {
    interval = Math.max(1, Math.ceil(interval * ease));
  } else if (q === 5) {
    ease += 0.15;
    interval = Math.max(1, Math.ceil(interval * ease * EASY_BONUS));
  }

  const retMod = 0.9 / retentionTarget;
  interval = Math.max(1, Math.ceil(interval * retMod * examMultiplier));
  reps += 1;

  s.ease = ease;
  s.interval = interval;
  s.reps = reps;
  s.state = 'review';
  s.due = addDays(now, interval).toISOString();
  return s;
}

export function previewIntervals(state: CardState, ctx: ReviewContext = {}) {
  return {
    0: review(state, 0, ctx).due,
    3: review(state, 3, ctx).due,
    4: review(state, 4, ctx).due,
    5: review(state, 5, ctx).due,
  } as Record<ReviewRating, string | null>;
}

export function buildQueue(
  cards: Card[],
  states: Record<string, CardState>,
  opts: { now?: Date; newLimit?: number; dueLimit?: number; weakChapterBoost?: Record<string, number> } = {},
): QueueResult {
  const now = opts.now ?? new Date();
  const newLimit = opts.newLimit ?? 20;
  const dueLimit = opts.dueLimit ?? 9999;
  const weakBoost = opts.weakChapterBoost ?? {};
  const due: Card[] = [];
  const learning: Card[] = [];
  const fresh: Card[] = [];

  for (const card of cards) {
    const state = states[card.id];
    if (state?.suspended) continue;
    if (!state || state.state === 'new') fresh.push(card);
    else if (state.state === 'learning' && (!state.due || new Date(state.due) <= now)) learning.push(card);
    else if (state.state === 'review' && state.due && new Date(state.due) <= now) due.push(card);
  }

  learning.sort((a, b) => new Date(states[a.id].due ?? 0).getTime() - new Date(states[b.id].due ?? 0).getTime());
  due.sort((a, b) => new Date(states[a.id].due ?? 0).getTime() - new Date(states[b.id].due ?? 0).getTime());

  if (Object.keys(weakBoost).length) {
    const score = (card: Card) => weakBoost[card.chapter] || 1;
    due.sort((a, b) => score(b) - score(a));
    fresh.sort((a, b) => score(b) - score(a));
  }

  const newCards = fresh.slice(0, newLimit);
  const dueCards = due.slice(0, dueLimit);
  const queue: Card[] = [];
  let li = 0;
  let di = 0;
  let ni = 0;

  while (li < learning.length) queue.push(learning[li++]);
  while (di < dueCards.length || ni < newCards.length) {
    for (let k = 0; k < 3 && di < dueCards.length; k += 1) queue.push(dueCards[di++]);
    if (ni < newCards.length) queue.push(newCards[ni++]);
  }

  return { queue, counts: { new: newCards.length, learning: learning.length, due: dueCards.length } };
}

export function formatDue(due: string | Date | null, now = new Date()): string {
  if (!due) return 'jetzt';
  const ms = (due instanceof Date ? due : new Date(due)).getTime() - now.getTime();
  if (ms < 0) return 'jetzt';
  const min = Math.round(ms / 60000);
  if (min < 60) return `< ${Math.max(1, min)}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  const days = Math.round(hr / 24);
  if (days < 30) return `${days}T`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}Mo`;
  return `${Math.round(days / 365)}J`;
}

function addMinutes(date: Date, minutes: number) {
  return new Date(date.getTime() + minutes * 60000);
}

function addDays(date: Date, days: number) {
  return new Date(date.getTime() + days * 86400000);
}
